/**
 * Card back rendering — single Artisan Mandala motif (dotted background + four
 * cardinal suit pips) on a solid color. Opponents differ by color only; see
 * DESIGN.md § Player Identity.
 *
 * `createCardBack` is the public entry (re-exported through `cards.js`).
 * `createBackSvg` is the internal entry used by `cards.js` for the face-down
 * branch of `createCard`.
 */

import { SVG_NS, s } from "./card-svg.js";
import { addArtisanPip } from "./card-pips.js";
import { getSharedDefs } from "./shared-defs.js";
import { cloneBackTemplate } from "./card-template-cache.js";
import { CARD_GEOMETRY } from "./card-geometry.js";

// The dot tile is identical for every back regardless of color (the pattern
// paints dots only; the bg rect underneath carries the color) — one shared
// pattern in the document-level defs instead of one per card.
const ARTISAN_PATTERN_ID = "artisan-dots";
let artisanPatternReady = false;

export function createCardBack(backColor = undefined) {
  // Cache consult: 3 back colors are fully deterministic — see card-template-cache.js.
  const cloned = cloneBackTemplate(backColor);
  if (cloned) return cloned;

  const div = document.createElement("div");
  div.className = "card face-down";
  div.appendChild(createBackSvg(backColor));
  return div;
}

export function createBackSvg(bgColor = "#082F49") {
  const svg = s("svg", { viewBox: "0 0 250 350", xmlns: SVG_NS });
  svg.appendChild(
    s("rect", { x: 0, y: 0, width: 250, height: 350, fill: bgColor }),
  );

  appendArtisanDotsOverlay(svg);

  svg.appendChild(
    s("rect", {
      x: 12,
      y: 12,
      width: 226,
      height: 326,
      rx: 12,
      ry: 12,
      fill: "none",
      stroke: "rgba(226, 232, 240, 0.25)",
      "stroke-width": 1.5,
    }),
  );

  const center = s("g", {
    fill: "none",
    stroke: "rgba(226, 232, 240, 0.22)",
    "stroke-width": 2,
  });
  addArtisanMandala(center);
  svg.appendChild(center);

  return svg;
}

/** Ensures the shared dot-tile pattern exists, then appends the overlay rect referencing it. */
function appendArtisanDotsOverlay(svg) {
  ensureArtisanDotsPattern();
  svg.appendChild(
    s("rect", {
      x: 0,
      y: 0,
      width: 250,
      height: 350,
      fill: `url(#${ARTISAN_PATTERN_ID})`,
    }),
  );
}

/** Mints the shared dot-tile pattern into the document-level defs, once. */
function ensureArtisanDotsPattern() {
  if (artisanPatternReady) return;
  const pat = document.createElementNS(SVG_NS, "pattern");
  pat.setAttribute("id", ARTISAN_PATTERN_ID);
  pat.setAttribute("width", "24");
  pat.setAttribute("height", "24");
  pat.setAttribute("patternUnits", "userSpaceOnUse");
  pat.appendChild(
    s("circle", { cx: 12, cy: 12, r: 1.2, fill: "rgba(226, 232, 240, 0.15)" }),
  );
  getSharedDefs().appendChild(pat);
  artisanPatternReady = true;
}

/**
 * Lays out the four cardinal Artisan suit pips (diamonds N, hearts S,
 * spades W, clubs E) inside a card-shaped container. Shared with the
 * Verdeckt prompt-tile in icons/prompt-icons.js.
 *
 * @param parent SVG group to append pips into.
 * @param geom { centerX, centerY, vRadius, hRadius, pipScale }
 *              N pip is at (centerX, centerY - vRadius); S at +vRadius;
 *              W at -hRadius; E at +hRadius. Coordinates assume the parent's
 *              userSpaceOnUse coordinate system.
 * @param pipColor the color string passed to addArtisanPip.
 */
export function addArtisanMandalaPips(parent, geom, pipColor) {
  const { centerX, centerY, vRadius, hRadius, pipScale } = geom;
  // Cardinal layout: diamonds N, hearts S, spades W, clubs E.
  const pips = [
    ["diamonds", centerX, centerY - vRadius],
    ["hearts", centerX, centerY + vRadius],
    ["spades", centerX - hRadius, centerY],
    ["clubs", centerX + hRadius, centerY],
  ];
  for (const [suit, x, y] of pips) {
    addArtisanPip(parent, suit, x, y, pipScale, pipColor);
  }
}

function addArtisanMandala(center) {
  addArtisanMandalaPips(
    center,
    {
      centerX: 125,
      centerY: 175,
      vRadius: 100, // vertical radius stays fixed — only the horizontal one is a golden candidate
      hRadius: CARD_GEOMETRY.mandalaHRadius,
      pipScale: 2.8,
    },
    "rgba(226, 232, 240, 0.25)",
  );
}
